import { BoxOfferItemEntity } from "@/modules/boxOffer/entities/boxOfferEntity"
import { BoxProgramVisibility } from "@/modules/boxProgrammation/boxProgramTypes"
import { getBoxProgramColorTypeWithHex } from "@/modules/boxProgrammation/boxProgramUtils"
import { boxProgramColors } from "@/modules/boxProgrammation/components/boxProgramColors"
import type { BoxProgramFormFields } from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import type {
  BoxProgramDetailsDto,
  BoxProgramItemDto,
  CreateBoxProgramInputDto,
  UpdateBoxProgramInputDto,
} from "@/modules/boxProgrammation/gateway/boxProgramDto"
import { MediaType } from "@/modules/common/media/mediaTypes"
import type { ProgramType } from "@/modules/programs/types/programTypes"
import type { HUImage } from "@/modules/types/imageTypes"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { UserEntity } from "@modules/profile/userProfile/userEntity"

export class BoxProgramItemEntity {
  id: string
  name: string
  image: HUImage | null
  owner: UserEntity
  color: string
  type: ProgramType
  visibility: BoxProgramVisibility | null

  private constructor(boxProgramItem: BoxProgramItemEntity) {
    this.id = boxProgramItem.id
    this.name = boxProgramItem.name
    this.image = boxProgramItem.image
    this.owner = boxProgramItem.owner
    this.color = boxProgramItem.color
    this.type = boxProgramItem.type
    this.visibility = boxProgramItem.visibility
  }

  static fromDto(dto: BoxProgramItemDto): BoxProgramItemEntity {
    return new BoxProgramItemEntity({
      id: dto.id,
      name: dto.name,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: UserEntity.fromDto(dto.owner),
      color: dto.color,
      type: dto.type,
      visibility: dto.visibility,
    })
  }
}

export type BoxProgramDetailsState = Omit<BoxProgramDetailsEntity, "toFormik">

export class BoxProgramDetailsEntity {
  id: string
  name: string
  description: string
  image: HUImage | null
  owner: UserEntity | null
  boxId: string
  color: string
  type: ProgramType
  visibility: BoxProgramVisibility | null
  defaultHideSessionContent: boolean
  restrictions: BoxOfferItemEntity[]

  private constructor(boxProgramDetails: BoxProgramDetailsState) {
    this.id = boxProgramDetails.id
    this.name = boxProgramDetails.name
    this.description = boxProgramDetails.description
    this.image = boxProgramDetails.image
    this.owner = boxProgramDetails.owner
    this.boxId = boxProgramDetails.boxId
    this.color = boxProgramDetails.color
    this.type = boxProgramDetails.type
    this.visibility = boxProgramDetails.visibility
    this.defaultHideSessionContent = boxProgramDetails.defaultHideSessionContent
    this.restrictions = boxProgramDetails.restrictions
  }

  static fromDto(dto: BoxProgramDetailsDto): BoxProgramDetailsEntity {
    return new BoxProgramDetailsEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: dto.owner ? UserEntity.fromDto(dto.owner) : null,
      boxId: dto.boxId,
      color: dto.color,
      type: dto.type,
      visibility: dto.visibility,
      defaultHideSessionContent: dto.defaultHideSessionContent,
      restrictions: dto.restrictions.map((restriction) => BoxOfferItemEntity.fromItemDto(restriction)),
    })
  }

  static fromFormikToCreateInputDto(formik: BoxProgramFormFields): CreateBoxProgramInputDto {
    return {
      name: formik.name,
      description: formik.description,
      imageKey: formik.image?.key ?? null,
      color: boxProgramColors[formik.color].background,
      visibility: formik.visibility,
      defaultHideSessionContent: formik.defaultHideSessionContent,
      offerIds: formik.offerIds,
    }
  }

  static fromFormikToUpdateInputDto(formik: BoxProgramFormFields): UpdateBoxProgramInputDto {
    return {
      name: formik.name,
      description: formik.description,
      imageKey: formik.image?.key ?? null,
      color: boxProgramColors[formik.color].background,
      visibility: formik.visibility,
      defaultHideSessionContent: formik.defaultHideSessionContent,
      offerIds: formik.offerIds,
    }
  }

  toFormik(): BoxProgramFormFields {
    return {
      name: this.name,
      description: this.description,
      image: {
        url: this.image?.url ?? "",
        key: this.image?.key ?? null,
        type: MediaType.S3_IMAGE,
        filename: null,
        isUploading: false,
        file: null,
      },
      color: getBoxProgramColorTypeWithHex(this.color),
      visibility: this.visibility ?? BoxProgramVisibility.MEMBERS,
      defaultHideSessionContent: this.defaultHideSessionContent,
      offerIds: this.restrictions.map((restriction) => restriction.id),
    }
  }
}
